import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * ImageUploader - reusable file→URL uploader.
 *
 * Props:
 *   value      - current URL string or array of URLs (when multiple=true)
 *   onChange   - (url|urls) => void
 *   multiple   - allow multiple uploads
 *   label      - eyebrow label
 *   max        - max images when multiple (default 4)
 */
export default function ImageUploader({ value, onChange, multiple = false, label = "Upload Image", max = 4, testid = "uploader" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const urls = multiple ? (Array.isArray(value) ? value : (value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [])) : (value ? [value] : []);
  const fullUrl = (u) => u;

  const upload = async (files) => {
    setBusy(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(data.url);
      }
      if (multiple) onChange([...urls, ...newUrls].slice(0, max));
      else onChange(newUrls[0]);
      toast.success(`${newUrls.length} file${newUrls.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (u) => {
    if (multiple) onChange(urls.filter((x) => x !== u));
    else onChange("");
  };

  return (
    <div data-testid={testid}>
      <span className="sq-eyebrow text-[10px]">{label}</span>
      <div className="mt-2 flex flex-wrap gap-2">
        {urls.map((u, i) => (
          <div key={i} className="relative w-20 h-20 border border-black/10 overflow-hidden" data-testid={`${testid}-img-${i}`}>
            <img src={fullUrl(u)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(u)} className="absolute top-1 right-1 bg-white p-0.5" data-testid={`${testid}-remove-${i}`}><X size={12} /></button>
          </div>
        ))}
        {urls.length < (multiple ? max : 1) && (
          <button
            type="button"
            data-testid={`${testid}-button`}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="w-20 h-20 border-2 border-dashed border-black/20 hover:border-squire-jeans flex items-center justify-center text-[#4B5563] hover:text-squire-jeans transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => upload(Array.from(e.target.files || []))}
      />
    </div>
  );
}
